import { memo } from 'react';

import { Check, Info } from 'lucide-react';

import { Tooltip } from '@/shared/components/tooltip';
import { COPY } from '@/shared/constants/copies';

import {
	COMPARE_PLAN_KEYS,
	COMPARE_PLAN_LABELS,
	PLAN_COMPARE_ROWS,
	type CompareCell,
	type CompareRow,
} from '../../constants/plan-compare';

const CompareCellValue = memo(function CompareCellValue({ cell }: { cell: CompareCell }) {
	if (cell.type === 'included') {
		return (
			<p className="flex justify-center text-base sm:text-sm">
				<span className="sr-only">{COPY.pricing.compareIncluded}</span>
				<Check
					aria-hidden
					className="size-4 shrink-0 text-primary"
				/>
			</p>
		);
	}

	if (cell.type === 'excluded') {
		return (
			<p className="text-center text-base text-muted-foreground sm:text-sm">
				<span className="sr-only">{COPY.pricing.compareNotIncluded}</span>
				<span aria-hidden>—</span>
			</p>
		);
	}

	return <p className="text-center text-base text-primary sm:text-sm">{cell.value}</p>;
});

const COMPARE_FEATURE_LINK_CLASS =
	'underline decoration-dashed decoration-secondary/40 underline-offset-[3px] transition-colors hover:decoration-secondary/70';

const CompareFeatureName = memo(function CompareFeatureName({ row }: { row: CompareRow }) {
	const name = row.href ? (
		<a
			href={row.href}
			target="_blank"
			rel="noreferrer"
			className={COMPARE_FEATURE_LINK_CLASS}
		>
			{row.feature}
		</a>
	) : (
		row.feature
	);

	if (!row.tooltip) return name;

	return (
		<span className="inline-flex items-start gap-2">
			{name}
			<Tooltip
				text={row.tooltip}
				className="!max-w-[270px]"
			>
				{/* `shrink-0` keeps the icon round when the feature name wraps. */}
				<Info className="size-4 shrink-0 translate-y-px cursor-default text-secondary hover:text-slate-600" />
			</Tooltip>
		</span>
	);
});

export const PlanCompareTable = memo(function PlanCompareTable() {
	return (
		<section className="mt-16">
			<div className="overflow-x-auto overflow-y-clip">
				<table className="w-full min-w-[640px] text-left">
					<thead>
						<tr className="border-b border-slate-950/10">
							<th className="whitespace-nowrap py-4 pr-6 text-lg font-semibold tracking-tight text-primary">
								{COPY.pricing.compareFeatureColumn}
							</th>
							{COMPARE_PLAN_KEYS.map((planKey) => (
								<th
									key={planKey}
									className="whitespace-nowrap px-3 py-4 text-center text-lg font-semibold tracking-tight text-primary"
								>
									{COMPARE_PLAN_LABELS[planKey]}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{PLAN_COMPARE_ROWS.map((row) => (
							<tr
								key={row.feature}
								className="border-b border-slate-950/5 last:border-b-0"
							>
								<th className="whitespace-normal py-3.5 pr-6 text-left text-base font-medium text-primary sm:text-sm">
									<CompareFeatureName row={row} />
								</th>
								{COMPARE_PLAN_KEYS.map((planKey) => (
									<td
										key={planKey}
										className="px-3 py-3.5"
									>
										<CompareCellValue cell={row.cells[planKey]} />
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</section>
	);
});
